import axios from 'axios';
import { API, toFullUrl } from '../../config/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Bell, Calendar, Clock, ExternalLink } from 'lucide-react';
import { FaSpotify } from 'react-icons/fa';
import LikeButton from '../LikeButton';
import FireButton from '../FireButton';

function formatReleaseDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getDaysUntilRelease(dateStr) {
  if (!dateStr) return null;
  const release = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((release - now) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : null;
}

export function UpcomingSection({
  upcomingTracks,
  presaveDialogOpen,
  setPresaveDialogOpen,
  selectedTrack,
  setSelectedTrack,
  presaveEmail,
  setPresaveEmail,
  presaveName,
  setPresaveName,
  presaving,
  setPresaving,
  onPresaveSuccess,
}) {
  const openPresaveDialog = (track) => {
    setSelectedTrack(track);
    setPresaveDialogOpen(true);
  };

  const handlePresave = async (e) => {
    e.preventDefault();
    if (!presaveEmail || !selectedTrack) return;
    setPresaving(true);
    try {
      const { data } = await axios.post(`${API}/presave`, {
        track_id: selectedTrack.id,
        email: presaveEmail,
        name: presaveName,
        notify_on_release: true,
      });
      toast.success(`Pre-saved! ${data.presave_count} fans are waiting for this release.`);
      setPresaveDialogOpen(false);
      setPresaveEmail('');
      setPresaveName('');
      if (selectedTrack.presave_link) {
        window.open(selectedTrack.presave_link, '_blank');
      }
      if (onPresaveSuccess) onPresaveSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pre-save');
    } finally {
      setPresaving(false);
    }
  };

  return (
    <>
      {upcomingTracks.length > 0 && (
        <section className="py-16 sm:py-24 px-4 sm:px-8 border-b border-[#262626] bg-[#0A0A0A]" data-testid="upcoming-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <div>
                <p className="overline text-[var(--primary,#FF3B30)] mb-2">COMING SOON</p>
                <h2 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tighter">
                  UPCOMING RELEASES
                </h2>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-[#A3A3A3]">
                <Bell className="w-5 h-5" />
                <span className="text-sm">Pre-save to get notified</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingTracks.map(track => {
                const daysUntil = getDaysUntilRelease(track.release_date);
                return (
                  <div
                    key={track.id}
                    className="relative bg-gradient-to-br from-[var(--primary,#FF3B30)]/20 to-[#141414] border border-[var(--primary,#FF3B30)]/30 p-0 overflow-hidden group"
                    data-testid={`upcoming-track-${track.id}`}
                  >
                    <div className="absolute top-4 left-4 z-10 bg-[var(--primary,#FF3B30)] px-3 py-1">
                      <span className="font-mono text-xs font-bold uppercase tracking-wider">COMING SOON</span>
                    </div>

                    <div className="relative aspect-square bg-[#262626] overflow-hidden">
                      {track.cover_image ? (
                        <img
                          src={toFullUrl(track.cover_image)}
                          alt={track.title}
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="font-cabinet text-6xl font-black text-[var(--primary,#FF3B30)]/30">
                            {track.title.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                    </div>

                    <div className="p-4">
                      <h3 className="font-cabinet text-xl font-bold uppercase tracking-tight mb-1">
                        {track.title}
                      </h3>
                      <p className="text-[#A3A3A3] text-sm mb-3">{track.artist}</p>

                      {track.release_date && (
                        <div className="flex items-center gap-4 mb-4 text-sm">
                          <div className="flex items-center gap-2 text-[#A3A3A3]">
                            <Calendar className="w-4 h-4 text-[var(--primary,#FF3B30)]" />
                            <span>{formatReleaseDate(track.release_date)}</span>
                          </div>
                          {daysUntil && (
                            <div className="flex items-center gap-2 text-[var(--primary,#FF3B30)]">
                              <Clock className="w-4 h-4" />
                              <span className="font-mono font-bold">{daysUntil} days</span>
                            </div>
                          )}
                        </div>
                      )}

                      {track.presave_count > 0 && (
                        <p className="text-xs text-[#A3A3A3] mb-3 font-mono">
                          {track.presave_count} fans waiting
                        </p>
                      )}

                      <Button
                        onClick={() => openPresaveDialog(track)}
                        className="w-full bg-[var(--primary,#FF3B30)] hover:bg-white hover:text-[#0A0A0A] rounded-none font-bold"
                        data-testid={`presave-btn-${track.id}`}
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        PRE-SAVE NOW
                      </Button>

                      {track.presave_link && (
                        <a
                          href={track.presave_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 mt-2 text-sm text-[#A3A3A3] hover:text-[var(--primary,#FF3B30)] transition-colors"
                          data-testid={`presave-external-${track.id}`}
                        >
                          <FaSpotify className="w-4 h-4" />
                          Pre-save on Spotify
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <div className="mt-3 flex items-center justify-center">
                        <LikeButton itemType="track" itemId={track.id} />
                        <FireButton itemType="track" itemId={track.id} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Pre-save Dialog */}
      <Dialog open={presaveDialogOpen} onOpenChange={setPresaveDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl font-black uppercase">
              PRE-SAVE "{selectedTrack?.title}"
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            {selectedTrack?.cover_image && (
              <div className="aspect-video bg-[#262626] mb-4 overflow-hidden">
                <img
                  src={toFullUrl(selectedTrack.cover_image)}
                  alt={selectedTrack.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <p className="text-[#A3A3A3] mb-4">
              Be the first to know when "{selectedTrack?.title}" drops. We'll notify you on release day!
            </p>

            {selectedTrack?.release_date && (
              <div className="flex items-center gap-2 text-[var(--primary,#FF3B30)] mb-4 font-mono text-sm">
                <Calendar className="w-4 h-4" />
                <span>Releases: {formatReleaseDate(selectedTrack.release_date)}</span>
              </div>
            )}

            <form onSubmit={handlePresave} className="space-y-4">
              <div>
                <Label className="overline">Your Email</Label>
                <Input
                  type="email"
                  value={presaveEmail}
                  onChange={(e) => setPresaveEmail(e.target.value)}
                  className="mt-2 bg-[#0A0A0A] border-[#262626] focus:border-[var(--primary,#FF3B30)] rounded-none"
                  placeholder="your@email.com"
                  required
                  data-testid="presave-email-input"
                />
              </div>
              <div>
                <Label className="overline">Your Name (Optional)</Label>
                <Input
                  type="text"
                  value={presaveName}
                  onChange={(e) => setPresaveName(e.target.value)}
                  className="mt-2 bg-[#0A0A0A] border-[#262626] focus:border-[var(--primary,#FF3B30)] rounded-none"
                  placeholder="Your name"
                  data-testid="presave-name-input"
                />
              </div>

              <Button
                type="submit"
                disabled={presaving}
                className="w-full bg-[var(--primary,#FF3B30)] hover:bg-white hover:text-[#0A0A0A] rounded-none h-12"
                data-testid="presave-submit-btn"
              >
                {presaving ? 'SAVING...' : 'PRE-SAVE & GET NOTIFIED'}
              </Button>
            </form>

            {selectedTrack?.presave_link && (
              <div className="mt-4 pt-4 border-t border-[#262626]">
                <a
                  href={selectedTrack.presave_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-[#1DB954] hover:bg-[#1ed760] text-white font-bold transition-colors"
                  data-testid="presave-spotify-btn"
                >
                  <FaSpotify className="w-5 h-5" />
                  Pre-save on Spotify
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
